// Mirrors app/routes/apps.santex.admin_.fabric.jsx's roundSantexPrice --
// snaps a raw rate*length figure to the same "clean number" steps the
// business's own price matrices use, so the pre-discount price shown here
// lines up visually with the post-discount price from the matrix.
function santexRoundPrice(value) {
  const base = Math.floor(value);
  const dec = value - base;
  const hundred = Math.round(dec * 100) / 100;
  const snap = (n) => base + n;

  if (hundred < 0.01) return base;
  if (hundred <= 0.12) return base;
  if (hundred <= 0.24) return snap(0.25);
  if (hundred <= 0.37) return snap(0.25);
  if (hundred <= 0.49) return snap(0.5);
  if (hundred <= 0.62) return snap(0.5);
  if (hundred <= 0.74) return snap(0.75);
  if (hundred <= 0.87) return snap(0.75);
  return snap(1);
}

// Bulk-roll discount tiers: each threshold replaces the previous one
// (not cumulative), matching app/routes/apps.santex.admin_.fabric.jsx's
// price-matrix generation.
function santexDiscountForLength(lengthM) {
  if (lengthM >= 89.6) return 0.55;
  if (lengthM >= 44.8) return 0.45;
  if (lengthM >= 22.4) return 0.35;
  return 0;
}

// Free returns apply only to exact multiples of the 3.2m base length
// (3.2, 16, 22.4, 44.8, 89.6, ...) -- working in whole "deci-meters"
// (lengthM * 10) avoids float rounding errors that a direct % 3.2 check
// would hit (e.g. 22.4 % 3.2 !== 0 in plain floating point).
function santexIsFreeReturnsLength(lengthM) {
  return Math.round(lengthM * 10) % 32 === 0;
}

// Shopify's App Proxy strips any Set-Cookie header an app returns (verified
// against the real store), so the in-progress order's id can't be tracked
// server-side via a cookie -- it's remembered here in localStorage instead,
// sent explicitly on each add, and echoed back in every response so this
// stays in sync (see utils/santexActiveOrder.server.js for the full story).
const SANTEX_ACTIVE_ORDER_KEY = 'santex_active_order';

function getSantexActiveOrderId() {
  try {
    return window.localStorage.getItem(SANTEX_ACTIVE_ORDER_KEY) || null;
  } catch {
    return null;
  }
}

function setSantexActiveOrderId(id) {
  try {
    window.localStorage.setItem(SANTEX_ACTIVE_ORDER_KEY, id);
  } catch {
    // ignore (private browsing etc.)
  }
}

let santexFabricTypesPromise = null;

function getSantexFabricTypes() {
  if (!santexFabricTypesPromise) {
    santexFabricTypesPromise = fetch('/apps/santex/fabric-types', {
      headers: { Accept: 'application/json' },
    })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load fabric types');
        return r.json();
      })
      .then((d) => d.fabrics || []);
  }
  return santexFabricTypesPromise;
}

if (!customElements.get('santex-length-buy')) {
  customElements.define(
    'santex-length-buy',
    class SantexLengthBuy extends HTMLElement {
      constructor() {
        super();
        this.productId = this.dataset.productId;
        this.isCustomDesign = this.dataset.isCustomDesign === 'true';
        this.select = this.querySelector('.santex-length-buy__select');
        this.priceContainer = document.getElementById(this.dataset.priceContainerId);
        this.errorEl = this.querySelector('.santex-length-buy__error');
        this.confirmationEl = this.querySelector('.santex-length-buy__confirmation');
        this.submitButton = this.querySelector('.santex-length-buy__submit');
        this.fabrics = [];
        this.currentMatrix = null;
        this.currentPriceLevel = null;
        this.suppressPriceObserver = false;
        this.priceObserver = null;

        this.onMaterialChange = this.onMaterialChange.bind(this);
        this.onLengthChange = this.onLengthChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
      }

      connectedCallback() {
        document.addEventListener('change', this.onMaterialChange);
        this.select.addEventListener('change', this.onLengthChange);
        this.submitButton.addEventListener('click', this.onSubmit);

        // Dawn's own variant-radios/product-form script owns this same
        // price container and, on every material change, re-renders it
        // itself -- asynchronously, after its own fetch -- with the newly
        // selected variant's bare "starting from" price. That lands *after*
        // our synchronous update and silently wipes out the length-aware
        // price the customer actually chose. Rather than race that timing,
        // watch the container (via its parent, in case Dawn replaces the
        // container node itself rather than just its contents) and
        // reassert our own price whenever something else changes it.
        const observeRoot = this.priceContainer && this.priceContainer.parentElement;
        if (observeRoot) {
          this.priceObserver = new MutationObserver(() => {
            if (this.suppressPriceObserver) return;
            this.updatePrice();
          });
          this.priceObserver.observe(observeRoot, { childList: true, subtree: true });
        }

        getSantexFabricTypes()
          .then((fabrics) => {
            this.fabrics = fabrics;
            this.refreshForMaterial();
          })
          .catch(() => {
            this.showError('Could not load pricing right now. Please refresh the page.');
          });
      }

      disconnectedCallback() {
        document.removeEventListener('change', this.onMaterialChange);
        if (this.priceObserver) this.priceObserver.disconnect();
      }

      getSelectedMaterial() {
        const checkedRadio = document.querySelector('input[name="Material"]:checked');
        if (checkedRadio) return checkedRadio.value;
        const dropdown = document.querySelector('select[name="Material"]');
        if (dropdown) return dropdown.value;
        return null;
      }

      onMaterialChange(event) {
        if (!event.target || event.target.name !== 'Material') return;
        this.refreshForMaterial();
      }

      refreshForMaterial() {
        this.hideError();
        const material = this.getSelectedMaterial();
        const fabric = this.fabrics.find((f) => f.fabric_handle === material);

        if (!fabric || !fabric.matrix || !Array.isArray(fabric.matrix.steps) || !fabric.matrix.steps.length) {
          this.currentMatrix = null;
          this.currentPriceLevel = null;
          this.select.disabled = true;
          this.select.innerHTML = '<option value="">Unavailable</option>';
          this.submitButton.disabled = true;
          return;
        }

        const previousLength = this.select.value;
        this.currentMatrix = fabric.matrix;
        this.currentPriceLevel = fabric.price_level;

        this.select.innerHTML = fabric.matrix.steps
          .map((step) => `<option value="${step.length_m}">${step.length_m} m</option>`)
          .join('');
        this.select.disabled = false;

        const stillAvailable = fabric.matrix.steps.some((s) => String(s.length_m) === previousLength);
        if (stillAvailable) {
          this.select.value = previousLength;
        }

        this.updatePrice();
      }

      onLengthChange() {
        this.updatePrice();
      }

      updatePrice() {
        if (!this.currentMatrix) return;
        const lengthM = Number(this.select.value);
        const step = this.currentMatrix.steps.find((s) => Number(s.length_m) === lengthM);
        if (!step) {
          this.submitButton.disabled = true;
          return;
        }
        this.submitButton.disabled = false;
        this.renderPriceBlock(lengthM, step);
      }

      // Rewrites the native price block (id "price-{section_id}", rendered
      // by snippets/price.liquid) in place, using the same classes that
      // snippet uses, so the real price the customer will pay always
      // matches what's displayed -- no separate/duplicate price line.
      renderPriceBlock(lengthM, step) {
        // Re-fetch by id rather than trusting the cached reference: if
        // Dawn's native update replaces the whole container node (instead
        // of just its children), the cached node would be a detached
        // orphan and writes to it would be invisible.
        const container = document.getElementById(this.dataset.priceContainerId) || this.priceContainer;
        if (!container) return;
        this.priceContainer = container;

        const currency = this.currentMatrix.currency || 'AED';
        const fmt = (amount) => `Dhs. ${Number(amount).toFixed(2)} ${currency}`;
        const price = Number(step.price);
        const unitPrice = price / lengthM;

        // Bulk-roll discounts: 35% at 22.4m+, 45% at 44.8m+, 55% at 89.6m+
        // (thresholds replace, not stack). price_level is the fabric's
        // undiscounted per-meter rate, so rate * length gives the "before"
        // price/unit-price to show struck through next to the real ones.
        const discount = santexDiscountForLength(lengthM);
        let original = null;
        if (discount > 0 && this.currentPriceLevel) {
          const candidate = santexRoundPrice(this.currentPriceLevel * lengthM);
          if (candidate > price) original = candidate;
        }

        const priceBlockHtml = original
          ? `
            <div class="price__sale">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span><s class="price-item price-item--regular">${fmt(original)}</s></span>
              <span class="visually-hidden visually-hidden--inline">Sale price</span>
              <span class="price-item price-item--sale price-item--last">${fmt(price)}</span>
              <span class="santex-length-buy__discount-badge">${Math.round(discount * 100)}% off</span>
            </div>
          `
          : `
            <div class="price__regular">
              <span class="visually-hidden visually-hidden--inline">Regular price</span>
              <span class="price-item price-item--regular">${fmt(price)}</span>
            </div>
          `;

        const unitPriceOriginal = original ? original / lengthM : null;
        const unitPriceHtml = `
          <small class="unit-price caption">
            <span class="visually-hidden">Unit price</span>
            <span class="price-item price-item--last">
              ${unitPriceOriginal ? `<s>${fmt(unitPriceOriginal)}</s> ` : ''}
              <span>${fmt(unitPrice)}</span>
              <span aria-hidden="true">/</span>
              <span>1 m</span>
            </span>
          </small>
        `;

        // Free returns: Santex catalog patterns only (never custom
        // designs -- this.isCustomDesign, set from the product's own
        // SANTEX_CUSTOM_DESIGN tag), and only at lengths that are exact
        // multiples of the 3.2m base length.
        const freeReturnsHtml =
          !this.isCustomDesign && santexIsFreeReturnsLength(lengthM)
            ? `
              <div class="santex-length-buy__free-returns">
                <span aria-hidden="true">&#10003;</span>
                <span>Free returns on this length</span>
              </div>
            `
            : '';

        this.suppressPriceObserver = true;
        this.priceContainer.innerHTML = `
          <div class="price price--large${original ? ' price--on-sale' : ''}">
            <div class="price__container">
              ${priceBlockHtml}
              ${unitPriceHtml}
              ${freeReturnsHtml}
            </div>
          </div>
        `;
        // Cleared on a macrotask (not a microtask/Promise), so it stays
        // true through the MutationObserver callback for *this* write --
        // that callback is scheduled as a microtask and would otherwise
        // see suppressPriceObserver already reset and loop back into
        // updatePrice() for our own change.
        setTimeout(() => {
          this.suppressPriceObserver = false;
        }, 0);
      }

      showError(message) {
        this.hideConfirmation();
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      hideError() {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      showConfirmation(itemCount, activeOrderId) {
        this.hideError();
        const cartHref = `/apps/santex/cart?activeOrderId=${encodeURIComponent(activeOrderId)}`;
        this.confirmationEl.innerHTML =
          `Added to your order (${itemCount} item${itemCount === 1 ? '' : 's'} so far). ` +
          `<a href="${cartHref}">View My Order</a>`;
        this.confirmationEl.hidden = false;
      }

      hideConfirmation() {
        this.confirmationEl.hidden = true;
        this.confirmationEl.innerHTML = '';
      }

      onSubmit() {
        const material = this.getSelectedMaterial();
        const lengthM = Number(this.select.value);

        if (!material || !Number.isFinite(lengthM) || lengthM <= 0) {
          this.showError('Please choose a material and length first.');
          return;
        }

        this.hideError();
        this.hideConfirmation();
        this.submitButton.disabled = true;
        const originalText = this.submitButton.textContent;
        this.submitButton.textContent = 'Adding…';

        fetch('/apps/santex/quote', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            productId: this.productId,
            material,
            length_m: lengthM,
            activeOrderId: getSantexActiveOrderId(),
          }),
        })
          .then(async (r) => {
            const json = await r.json();
            if (!r.ok || !json.ok) {
              throw new Error(json.error || 'Could not add this to your order. Please try again.');
            }
            setSantexActiveOrderId(json.activeOrderId);
            this.showConfirmation(json.itemCount, json.activeOrderId);
          })
          .catch((err) => {
            this.showError(err.message || 'Something went wrong. Please try again.');
          })
          .finally(() => {
            this.submitButton.disabled = false;
            this.submitButton.textContent = originalText;
          });
      }
    },
  );
}
